import { useMemo } from "react"
import dynamic from "next/dynamic"
import { useTranslations } from "next-intl"

import useInject from "~/hooks/useInject"
import { TRAFFIC_STATES_URL_SLUGS } from "~/utilities/constants"

const LoginModal = dynamic(() => import("~/components/auth/LoginModal"))
const SignupModal = dynamic(() => import("~/components/auth/SignUpModal"))

const mapStore = store => ({
  isLoggedIn: store.auth.isLoggedIn,
  logout: store.auth.logout,
  isPayNYCFlow: store.ui.isPayNYCFlow
})

const useFooterLinks = () => {
  const translate = useTranslations('footer.links')
  const { isLoggedIn, logout, isPayNYCFlow } = useInject(mapStore)
  const allowedStates = ['ca', 'ny', 'wa', 'tx', 'ga', 'nj', 'nc', 'mo']

  // paynyctickets.com is a parking-only brand: the per-state traffic-ticket
  // landing links don't belong in its footer (the targets 301 to "/" anyway).
  const trafficTicketInfoSection = {
    title: translate('trafficTicketInfo'),
    sizes: {
      sm: 4,
      md: 2,
      lg: 2
    },
    links: Object.keys(TRAFFIC_STATES_URL_SLUGS)
      .filter(key => allowedStates.includes(key)) // keep only allowed states
      .map(key => ({
        title: translate('cityTrafficTicket', { stateSlug: TRAFFIC_STATES_URL_SLUGS[key].title }),
        href: `/${key}-traffic-tickets`,
        external: false
      }))
  }

  const mainLinks = [
    ...(isPayNYCFlow ? [] : [trafficTicketInfoSection]),
    {
      title: translate('winIt'),
      sizes: {
        sm: 6,
        md: 2,
        lg: 2
      },
      links: [
        {
          title: translate('blog'),
          href: "https://blog.appwinit.com",
          external: true
        },
        {
          title: translate('chicago'),
          href: `https://paychicagotickets.com`,
          external: true
        },
        {
          title: translate('careers'),
          href: `/careers`
        }
      ]
    },
    {
      title: translate('support'),
      sizes: {
        sm: 6,
        md: 2,
        lg: 2
      },
      links: [
        {
          title: translate('howItWorks'),
          href: "/how-it-works",
          external: false
        },
        {
          title: translate('faq'),
          href: "/faq",
          external: false
        },
        {
          title: translate('cancellationRefund'),
          href: "/cancellation-refund-policy",
          external: false
        },
        {
          title: translate('contact'),
          href: "/contact",
          external: false
        }
      ]
    }
  ]

  const baseAccountLinks = {
    title: translate('account'),
    sizes: {
      sm: 6,
      md: 3,
      lg: 3
    },
    links: [
      {
        title: translate('parkingTickets'),
        href: `/dashboard/parking`,
        external: false
      },
      {
        title: translate('trafficTickets'),
        href: `/dashboard/traffic`,
        external: false
      },
      {
        title: translate('profile'),
        href: `/dashboard/profile`,
        external: false
      },
      {
        title: translate('plateManagement'),
        href: `/dashboard/plate-management`,
        external: false
      }
    ]
  }

  const links = useMemo(() => {
    const result = [ ...mainLinks ]
    const account = { ...baseAccountLinks }
    account.links = [ ...baseAccountLinks.links ]

    if (isLoggedIn) {
      account.links.push({
        title: translate('logout'),
        onClick: logout
      })
    } else {
      account.links.unshift(
        {
          title: translate('login'),
          opener: true,
          Component: LoginModal
        },
        {
          title: translate('signUp'),
          opener: true,
          Component: SignupModal
        }
      )
    }
    result.push(account)
    return result
  }, [isLoggedIn])

  return links
}

export default useFooterLinks
